import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import styles from "./EmailPicker.module.scss";
import { colourStyles } from "../../form/Select/Select";
import useEmailsState from "../../../model/useEmailsState";
import SelectOptionEmail from "../../SelectOption/SelectOptionEmail";
import useBuyFormStore from "stores/useBuyFormStore";
import { EmailPickerProps } from "types/emailPicker.type";

const EmailPicker: React.FC<EmailPickerProps> = ({ name, defaultValue, inputRef, placeholder, isDisabled = false }) => {
    const customInputRef = useRef<HTMLInputElement | null>(null);

    const { t } = useTranslation();
    const { emails } = useEmailsState();
    const { email, setEmail, setHasAutoEmail } = useBuyFormStore();

    const [isCustom, setIsCustom] = useState(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (inputRef) {
            inputRef.current = customInputRef.current;
        }
    }, [inputRef]);

    useEffect(() => {
        if (!initialized && !email && defaultValue) {
            !isCustom && setEmail(defaultValue);
            setHasAutoEmail(true);
        }

        if (!initialized && !defaultValue) {
            setEmail(email || "");
            setIsCustom(true);
        }

        if (initialized && email && emails.length > 0 && !emails.some((e) => e.email === email)) {
            setIsCustom(true);
        }

        setInitialized(true);
    }, [emails, email, isCustom, initialized, setEmail, setHasAutoEmail]);

    const customStyles = useMemo(
        () => ({
            ...colourStyles,
            control: (provided: any, state: any) => ({
                ...colourStyles.control(provided, state),
                backgroundColor: isCustom ? "#F3F3F5" : state.isFocused ? "#fff" : "#F3F3F5",
            }),
        }),
        [isCustom]
    );

    const options = useMemo(() => {
        const emailOptions = emails.map((email) => ({
            value: email.email,
            label: email.email,
            isDisabled: false,
        }));

        emailOptions.push({
            value: "custom",
            label: t("EmailPickerComponent.newEmail"),
            isDisabled: false,
        });

        return emailOptions;
    }, [emails, t]);

    const selectedOption = useMemo(() => {
        if (!email) return null;

        const foundEmail = emails.find((e) => e.email === email);

        if (foundEmail) {
            return {
                value: foundEmail.email,
                label: foundEmail.email,
            };
        }

        if (email && isCustom) {
            return {
                value: "custom",
                label: t("EmailPickerComponent.newEmail"),
            };
        }

        return null;
    }, [email, emails, isCustom, t]);

    const handleChange = useCallback(
        (newValue: any) => {
            if (!newValue) {
                setEmail("");
                setIsCustom(false);
                return;
            }

            if (newValue.value !== "custom") {
                setEmail(newValue.value);
                setIsCustom(false);
            } else {
                setEmail("");
                setIsCustom(true);

                setTimeout(() => {
                    customInputRef.current?.focus();
                }, 1);
            }
        },
        [setEmail]
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value;
            setEmail(value);
        },
        [setEmail]
    );

    return (
        <div className={styles._container}>
            <Select
                name={name}
                options={options}
                styles={customStyles}
                isSearchable={false}
                isDisabled={isDisabled}
                placeholder={placeholder || t("buy.emailSelectPlaceholder")}
                components={{
                    IndicatorSeparator: () => null,
                    Option: SelectOptionEmail,
                }}
                value={selectedOption}
                onChange={handleChange}
            />
            {isCustom && (
                <input
                    ref={(el) => {
                        customInputRef.current = el;
                        if (inputRef) inputRef.current = el;
                    }}
                    className={styles.customSpz}
                    value={email}
                    onChange={handleInputChange}
                    placeholder={t("EmailPickerComponent.email")}
                    type="email"
                    autoComplete="email"
                />
            )}
        </div>
    );
};

export default React.memo(EmailPicker);
